import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { LiveTipCard } from './LiveTipCard'
import { LiveTipsFilterBar, type LiveTipsFilters, DEFAULT_FILTERS } from './LiveTipsFilterBar'
import { useLiveTips, useTipStatistics } from '../swr-config'
import { useLiveTipsNotification } from '../context/LiveTipsContext'
import { useFeatureTracking, FeatureNames } from '../hooks/useActivityTracking'
import { IoRefresh, IoFilter } from 'react-icons/io5'

const LIMIT = 50

export function LiveTipsPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.LIVE_TIPS)
  const [filters, setFilters] = useState<LiveTipsFilters>(DEFAULT_FILTERS)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [showFilters, setShowFilters] = useState(false)

  // WebSocket connection for real-time updates
  const wsRef = useRef<WebSocket | null>(null)
  const [wsConnected, setWsConnected] = useState(false)
  const [wsError, setWsError] = useState<string | null>(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const reconnectTimeoutRef = useRef<number | null>(null)
  const pingIntervalRef = useRef<number | null>(null)

  // Build SWR params from filters
  const swrParams = useMemo(() => ({
    status: filters.status === 'all' ? undefined : filters.status,
    marketCategories: filters.marketCategories,
    minProbability: filters.minProbability,
    limit: LIMIT,
    offset: page * LIMIT,
  }), [filters, page])

  // SWR hooks
  const { data, error, isLoading, mutate } = useLiveTips(swrParams)
  const { data: stats, error: statsError } = useTipStatistics()

  // Live tips notification context
  const { notifyNewTip, markTipsSeen } = useLiveTipsNotification()

  // Mark tips as seen when this page mounts
  useEffect(() => {
    markTipsSeen()
  }, [markTipsSeen])

  // Connect to WebSocket for real-time tip updates with reconnection
  useEffect(() => {
    const wsBase = (import.meta as any).env?.VITE_WS_BASE ?? 'ws://localhost:8005/v1'

    const connect = () => {
      // Clean up any existing connection
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current)
      }

      const ws = new WebSocket(`${wsBase}/ws/tips`)

      ws.onopen = () => {
        setWsConnected(true)
        setWsError(null)
        reconnectAttempts.current = 0
        console.log('[LiveTipsPage] WebSocket connected')

        // Heartbeat ping every 30s
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          if (msg.type === 'new_tip') {
            mutate()
            notifyNewTip() // Show green badge on nav
          } else if (msg.type === 'tip_settled' || msg.type === 'tip_invalidated') {
            mutate()
          }
        } catch (e) {
          console.warn('[LiveTipsPage] Invalid WebSocket message:', e)
        }
      }

      ws.onclose = (event) => {
        setWsConnected(false)
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
        }

        // Don't reconnect if intentionally closed
        if (event.code === 1000) return

        // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max 30s)
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
          setWsError(`Reconnecting in ${Math.round(delay / 1000)}s...`)
          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectAttempts.current++
            connect()
          }, delay)
        } else {
          setWsError('Connection lost. Click to retry.')
        }
      }

      ws.onerror = () => {
        setWsError('Connection error')
      }

      wsRef.current = ws
    }

    connect()

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
      wsRef.current?.close(1000)
    }
  }, [mutate, notifyNewTip])

  // Manual reconnect handler
  const handleReconnect = useCallback(() => {
    reconnectAttempts.current = 0
    setWsError(null)
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    // Trigger reconnect by closing and reopening
    wsRef.current?.close()
    const wsBase = (import.meta as any).env?.VITE_WS_BASE ?? 'ws://localhost:8005/v1'
    const ws = new WebSocket(`${wsBase}/ws/tips`)

    ws.onopen = () => {
      setWsConnected(true)
      setWsError(null)
      reconnectAttempts.current = 0
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 30000)
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'new_tip') {
          mutate()
          notifyNewTip()
        } else if (msg.type === 'tip_settled' || msg.type === 'tip_invalidated') {
          mutate()
        }
      } catch (e) {
        console.warn('[LiveTipsPage] Invalid WebSocket message:', e)
      }
    }

    ws.onclose = () => setWsConnected(false)
    ws.onerror = () => setWsError('Connection error')
    wsRef.current = ws
  }, [mutate, notifyNewTip])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [filters.status, filters.marketCategories, filters.minProbability])

  const handleToggle = useCallback((tipId: number) => {
    setExpandedId((prev) => (prev === tipId ? null : tipId))
  }, [])

  const totalPages = data ? Math.ceil(data.total_count / LIMIT) : 0

  // Only show error if it's a real failure (not just empty data)
  // Network errors, server errors, etc. should be shown
  const isNetworkError = error && (
    error.message?.includes('fetch') ||
    error.message?.includes('network') ||
    error.message?.includes('API error 5') // 5xx server errors
  )
  const errorMessage = isNetworkError ? 'Unable to load tips. Please check your connection.' : null

  return (
    <div className="live-tips-page">
      <div className="card">
        <div className="card-inner">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-main">
              <h1>Live Tips</h1>
              <div className="header-actions">
                <span className={`ws-indicator ${wsConnected ? 'connected' : 'disconnected'}`} title={wsConnected ? 'Real-time updates active' : 'Reconnecting...'}>
                  {wsConnected ? 'Live' : 'Offline'}
                </span>
                <button
                  className="refresh-btn"
                  onClick={() => mutate()}
                  disabled={isLoading}
                  title="Refresh"
                >
                  <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
                </button>
                <button
                  className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                  title="Toggle filters"
                >
                  <IoFilter size={18} />
                </button>
              </div>
            </div>
            <p className="page-subtitle">
              High-probability tips for live matches based on pre-match analysis
            </p>
          </div>

          {/* Connection Banner */}
          {!wsConnected && wsError && (
            <div className="connection-banner disconnected">
              <span>{wsError}</span>
              {reconnectAttempts.current >= maxReconnectAttempts && (
                <button onClick={handleReconnect}>Retry</button>
              )}
            </div>
          )}

          {/* Statistics Summary - only show if stats loaded successfully */}
          {stats && !statsError && (
            <div className="tips-stats-bar">
              <div className="stat-item">
                <span className="stat-value">{stats.active_tips}</span>
                <span className="stat-label">Active</span>
              </div>
              <div className="stat-item landed">
                <span className="stat-value">{stats.landed_tips}</span>
                <span className="stat-label">Landed</span>
              </div>
              <div className="stat-item missed">
                <span className="stat-value">{stats.missed_tips}</span>
                <span className="stat-label">Missed</span>
              </div>
              {stats.accuracy_percent != null && (
                <div className="stat-item accuracy">
                  <span className="stat-value">{stats.accuracy_percent.toFixed(0)}%</span>
                  <span className="stat-label">Accuracy</span>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <LiveTipsFilterBar filters={filters} onChange={setFilters} />
          )}

          {/* Loading State */}
          {isLoading && !data && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading tips...</p>
            </div>
          )}

          {/* Error State - only for network/server errors */}
          {errorMessage && (
            <div className="error-state">
              <p>{errorMessage}</p>
              <button onClick={() => mutate()}>Retry</button>
            </div>
          )}

          {/* Tips List - show when data is loaded (even if empty) */}
          {!errorMessage && data && (
            <>
              <div className="results-summary">
                Showing {data.tips.length} of {data.total_count} tips
              </div>

              <div className="live-tips-list">
                {data.tips.map((tip) => (
                  <LiveTipCard
                    key={tip.id}
                    tip={tip}
                    isExpanded={expandedId === tip.id}
                    onToggle={() => handleToggle(tip.id)}
                  />
                ))}
              </div>

              {data.tips.length === 0 && (
                <div className="empty-state">
                  {filters.status === 'active' ? (
                    <>
                      <p>No active tips at the moment.</p>
                      <p>Tips are generated during live matches for high-probability selections.</p>
                    </>
                  ) : (
                    <>
                      <p>No tips match your filters.</p>
                      <p>Try adjusting your filters or check back during live matches.</p>
                    </>
                  )}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                    Previous
                  </button>
                  <span>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {/* Fallback empty state when error occurs but it's not a network error */}
          {!isLoading && !data && !errorMessage && (
            <div className="empty-state">
              <p>No live tips available right now.</p>
              <p>Check back when matches are in play for real-time tips.</p>
              <button className="retry-btn" onClick={() => mutate()}>
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
